// Build the Meta Manual Advanced Matching (MAM) payload from the
// in-flight quote / lead form data. Meta's fbq() auto-hashes these
// fields when passed to init or track(), so we send plaintext after
// normalization per Meta's Customer Information Parameters spec:
// https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
//
// Each field is normalized to the format Meta expects BEFORE hashing
// so the hashed value matches what other Meta data sources produce
// for the same person. Mismatched normalization is the #1 reason
// EMQ stays low: a value that fails Meta's hash-match check counts
// as if it was never sent.
//
// Pushed to window.dataLayer under the 'meta_user_data' key alongside
// the existing 'formData' so the GTM Meta Pixel Lead-event tag can
// read these directly. See kenjimax/Cabana-Tasks#507 for the audit
// thread.

const US_STATE_NAME_TO_CODE = {
  'alabama': 'al', 'alaska': 'ak', 'arizona': 'az', 'arkansas': 'ar',
  'california': 'ca', 'colorado': 'co', 'connecticut': 'ct',
  'delaware': 'de', 'district of columbia': 'dc', 'florida': 'fl',
  'georgia': 'ga', 'hawaii': 'hi', 'idaho': 'id', 'illinois': 'il',
  'indiana': 'in', 'iowa': 'ia', 'kansas': 'ks', 'kentucky': 'ky',
  'louisiana': 'la', 'maine': 'me', 'maryland': 'md',
  'massachusetts': 'ma', 'michigan': 'mi', 'minnesota': 'mn',
  'mississippi': 'ms', 'missouri': 'mo', 'montana': 'mt',
  'nebraska': 'ne', 'nevada': 'nv', 'new hampshire': 'nh',
  'new jersey': 'nj', 'new mexico': 'nm', 'new york': 'ny',
  'north carolina': 'nc', 'north dakota': 'nd', 'ohio': 'oh',
  'oklahoma': 'ok', 'oregon': 'or', 'pennsylvania': 'pa',
  'rhode island': 'ri', 'south carolina': 'sc', 'south dakota': 'sd',
  'tennessee': 'tn', 'texas': 'tx', 'utah': 'ut', 'vermont': 'vt',
  'virginia': 'va', 'washington': 'wa', 'west virginia': 'wv',
  'wisconsin': 'wi', 'wyoming': 'wy',
};

// Email: lowercase, trim. Reject if no @ or no dot after @.
function normalizeEmail(raw) {
  const s = String(raw || '').trim().toLowerCase();
  const at = s.indexOf('@');
  if (at <= 0) return null;
  const dot = s.indexOf('.', at);
  if (dot <= at + 1) return null;
  // Reject obvious test/placeholder values that hash but never match.
  if (s === 'test@test.com' || s.startsWith('noreply@')) return null;
  return s;
}

// Phone: per Meta spec, digits only with country code prefix, no
// '+', no spaces, no extension. US numbers must start with '1'. We
// reject 7-digit local numbers, numbers below 10 digits, and any
// payload that looks like an extension (e.g. "+1 760 201 9135 x123").
// US country code is the only one we infer; anything starting with
// a non-1 leading digit and exactly 11+ digits is passed through.
function normalizePhone(raw) {
  if (raw == null) return null;
  const str = String(raw);
  // Reject extensions explicitly to avoid silently sending bad data.
  if (/\bext\b|\bx\d|#\d/i.test(str)) return null;
  const digits = str.replace(/\D/g, '');
  if (digits.length === 10) return '1' + digits;
  if (digits.length === 11 && digits.startsWith('1')) return digits;
  // International numbers (E.164 without +). Accept 11-15 digits when
  // not starting with 1 (Meta's documented format).
  if (digits.length >= 11 && digits.length <= 15) return digits;
  return null;
}

// First/last name: lowercase, trim. Strip whitespace and any non-
// letter or hyphen characters (Meta lowercases AND strips non-
// alphabetic). Hyphens, apostrophes, accents stay (Meta hashes them
// as-is). Names below 1 char are rejected.
function normalizeName(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  // Strip leading/trailing punctuation; keep internal hyphens and
  // apostrophes. Meta accepts unicode letters; trust the input shape.
  const cleaned = s.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, '');
  return cleaned || null;
}

// Zip: digits-only first 5 (Meta wants 5-digit US ZIP, no zip+4).
function normalizeZip(raw) {
  if (raw == null) return null;
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length < 5) return null;
  return digits.slice(0, 5);
}

// City: per Meta spec, lowercase + remove ALL spaces and punctuation.
// "San Diego" becomes "sandiego". This matters because Meta's hash
// match requires byte-identical input.
function normalizeCity(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase().replace(/[\s\p{P}]+/gu, '');
  return s || null;
}

// State: per Meta spec, 2-character ANSI code, lowercase. Accept
// full state names (mapped via US_STATE_NAME_TO_CODE) or 2-char codes.
function normalizeState(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (s.length === 2) return s;
  if (US_STATE_NAME_TO_CODE[s]) return US_STATE_NAME_TO_CODE[s];
  return null;
}

// Country: per Meta spec, 2-character lowercase ISO code. Default US
// when name matches "united states" / "usa" / "us". Anything else is
// passed through as 2-char lowercase if it already looks like one.
function normalizeCountry(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (s.length === 2) return s;
  if (s === 'united states' || s === 'usa' || s === 'us' || s === 'united states of america') {
    return 'us';
  }
  return null;
}

export function buildMetaUserData(formData) {
  if (!formData || typeof formData !== 'object') return null;
  const out = {};

  const em = normalizeEmail(
    formData.email
      || formData.estimateEmail
      || formData.commercialEmail
      || formData.personEmail
  );
  if (em) out.em = em;

  const ph = normalizePhone(
    formData.phone
      || formData.personPhone
      || formData.estimatePhone
      || formData.commercialPhone
  );
  if (ph) out.ph = ph;

  // First and last name. Prefer explicit fields, fall back to splitting
  // a single personName / fullName.
  let fn = formData.firstName ? normalizeName(formData.firstName) : null;
  let ln = formData.lastName ? normalizeName(formData.lastName) : null;
  if (!fn || !ln) {
    const full = String(formData.personName || formData.fullName || '').trim();
    if (full) {
      const parts = full.split(/[\s,]+/).filter(Boolean);
      if (parts.length === 1) {
        fn = fn || normalizeName(parts[0]);
      } else if (parts.length >= 2) {
        fn = fn || normalizeName(parts[0]);
        ln = ln || normalizeName(parts[parts.length - 1]);
      }
    }
  }
  if (fn) out.fn = fn;
  if (ln) out.ln = ln;

  const zp = normalizeZip(formData.zipCode || formData.zip || formData.postalCode);
  if (zp) out.zp = zp;

  const ct = normalizeCity(formData.city);
  if (ct) out.ct = ct;

  const st = normalizeState(formData.state);
  if (st) out.st = st;

  // Country defaults to US for Cabana's footprint when state is set
  // but country was not.
  const co = normalizeCountry(formData.country) || (st ? 'us' : null);
  if (co) out.country = co;

  return Object.keys(out).length ? out : null;
}

// Exported for unit tests / runtime introspection.
export const __internal = {
  normalizeEmail, normalizePhone, normalizeName, normalizeZip,
  normalizeCity, normalizeState, normalizeCountry,
  US_STATE_NAME_TO_CODE,
};
